import { HttpClient } from "@angular/common/http";
import { inject, Injectable, SecurityContext } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { JwtHelperService } from "@auth0/angular-jwt";
import { UserModel, UserSchema } from "app/models/user.model";
import { BehaviorSubject, from, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { z } from "zod/v4";

import * as Enums from "../../enums/enums";
import { TextcatTextfield } from "../../enums/enums";
import { RegionConfiguration, RegionConfigurationSchema } from "../../models/region-configuration.model";
import { ServerConfiguration } from "../../models/server-configuration.model";
import { ServerModel, ServerSchema } from "../../models/server.model";
import * as albinaApi from "../albina-api";
import { LocalStorageService } from "../local-storage-service/local-storage.service";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  private localStorageService = inject(LocalStorageService);
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);

  private authentication: AuthenticationResponse;
  private internalRegions: RegionConfiguration[] = [];
  private externalServers: ServerModel[];
  private jwtHelper: JwtHelperService;
  private activeRegion: RegionConfiguration | undefined;
  private activeRegionSubject = new BehaviorSubject<RegionConfiguration | undefined>(undefined);
  public readonly activeRegion$ = this.activeRegionSubject.asObservable();

  constructor() {
    this.externalServers = [];
    try {
      this.authentication = this.localStorageService.getCurrentAuthor();
    } catch {
      this.localStorageService.setCurrentAuthor(undefined);
    }
    try {
      this.setActiveRegion(this.localStorageService.getActiveRegion());
    } catch {
      this.localStorageService.setActiveRegion(undefined);
    }
    try {
      this.setInternalRegions(this.localStorageService.getInternalRegions());
    } catch {
      this.localStorageService.setInternalRegions(undefined);
    }
    try {
      this.setExternalServers(this.localStorageService.getExternalServers());
    } catch {
      this.localStorageService.setExternalServers(undefined);
    }
    this.jwtHelper = new JwtHelperService();
  }

  public login(username: string, password: string): Observable<boolean> {
    return from(albinaApi.login({ body: { username, password }, throwOnError: true })).pipe(
      map((res) => {
        const data = AuthenticationResponseSchema.or(AuthenticationResponseSchema2024).parse(res.data, {
          reportInput: true,
        });
        if (!data.access_token) {
          return false;
        }
        this.authentication = data;
        this.localStorageService.setCurrentAuthor(data);
        const authorRegions = this.getCurrentAuthorRegions();
        const activeRegionFromLocalStorage = this.localStorageService.getActiveRegion();
        this.setActiveRegion(
          authorRegions.find((r) => r.id === activeRegionFromLocalStorage?.id) ?? authorRegions?.[0],
        );
        this.loadInternalRegions();
        this.externalServerLogins();
        return true;
      }),
    );
  }

  get accessToken(): string {
    return this.authentication?.access_token;
  }

  public isUserLoggedIn(): boolean {
    return this.accessToken && !this.jwtHelper.isTokenExpired(this.accessToken);
  }

  public logout() {
    console.debug("[" + this.currentAuthor?.name + "] Logged out!");
    this.authentication = null;
    this.activeRegion = undefined;
    this.activeRegionSubject.next(undefined);
    this.localStorageService.setCurrentAuthor(undefined);
    this.localStorageService.setInternalRegions(undefined);
    this.localStorageService.setExternalServers(undefined);
    this.externalServers = [];
  }

  private loadInternalRegions(): void {
    from(albinaApi.getRegions({ throwOnError: true })).subscribe((res) =>
      this.setInternalRegions(res.data as unknown as RegionConfiguration[]),
    );
  }

  private setInternalRegions(regions: RegionConfiguration[]) {
    this.internalRegions = RegionConfigurationSchema.array().parse(regions);
    this.localStorageService.setInternalRegions(this.internalRegions);
  }

  private externalServerLogins() {
    from(albinaApi.getExternalServerConfigurations({ throwOnError: true })).subscribe(
      (res) => {
        for (const entry of res.data as unknown as ServerConfiguration[]) {
          this.externalServerLogin(entry).subscribe(
            (data) => {
              if (data === true) {
                console.debug("[" + entry.name + "] Logged in!");
              } else {
                console.error("[" + entry.name + "] Login failed!");
              }
            },
            (error) => {
              console.error("[" + entry.name + "] Login failed: " + JSON.stringify(error._body), error);
            },
          );
        }
      },
      (error) => {
        console.error("External server instances could not be loaded: " + JSON.stringify(error._body), error);
      },
    );
  }

  public externalServerLogin(server: ServerConfiguration): Observable<boolean> {
    if (server.userName.startsWith("https://")) {
      const tokenURL = server.userName;
      const headers = { "Content-Type": "application/x-www-form-urlencoded" };
      return this.http
        .post<{ access_token: string }>(tokenURL, server.password, { headers })
        .pipe(map((data) => this.addExternalServer(server, data)));
    }
    const url = server.apiUrl + "authentication";
    const body = JSON.stringify({ username: server.userName, password: server.password });
    return this.http.post(url, body).pipe(
      map((json) => {
        const data = AuthenticationResponseSchema.or(AuthenticationResponseSchema2024).parse(json);
        return this.addExternalServer(server, data);
      }),
    );
  }

  private addExternalServer(
    server0: ServerConfiguration,
    json: AuthenticationResponse | { access_token: string },
  ): boolean {
    if (!json.access_token) {
      return false;
    }
    const server = ServerSchema.parse({
      ...server0,
      regions: (json as AuthenticationResponse).regions?.map((r) => r.id),
      accessToken: json.access_token,
    });
    this.externalServers.push(server);
    this.localStorageService.setExternalServers(this.externalServers);
    return true;
  }

  private setExternalServers(json: ServerModel[]) {
    if (!json) {
      return;
    }
    for (const server of json) {
      this.externalServers.push(server);
    }
  }

  public checkExternalServerLogin() {
    for (const server of this.externalServers) {
      if (this.jwtHelper.isTokenExpired(server.accessToken)) {
        this.externalServers = [];
        this.localStorageService.setExternalServers(undefined);
        this.externalServerLogins();
        break;
      }
    }
  }

  get currentAuthor(): UserModel {
    return this.authentication?.user;
  }

  public getCurrentAuthor(): UserModel {
    return this.currentAuthor;
  }

  public getUsername(): string {
    return this.currentAuthor?.name;
  }

  public getEmail(): string {
    return this.currentAuthor?.email;
  }

  public getUserImage() {
    return this.currentAuthor?.image;
  }

  public getUserImageSanitized(image) {
    if (image) {
      return image.startsWith("data")
        ? this.sanitizer.sanitize(SecurityContext.URL, image)
        : this.sanitizer.sanitize(SecurityContext.URL, "data:image/jpeg;base64," + image);
    } else {
      return null;
    }
  }

  public getActiveRegionAvalancheProblems(): Enums.AvalancheProblem[] {
    return [
      Enums.AvalancheProblem.new_snow,
      Enums.AvalancheProblem.wind_slab,
      Enums.AvalancheProblem.persistent_weak_layers,
      Enums.AvalancheProblem.wet_snow,
      Enums.AvalancheProblem.gliding_snow,
      this.getActiveRegion()?.enableAvalancheProblemCornices && Enums.AvalancheProblem.cornices,
      this.getActiveRegion()?.enableAvalancheProblemNoDistinctAvalancheProblem &&
        Enums.AvalancheProblem.no_distinct_avalanche_problem,
    ].filter((p) => !!p);
  }

  public isTextfieldEnabled(textfield: keyof typeof TextcatTextfield): boolean {
    return (
      this.getActiveRegion()?.enabledTextcatFields?.includes(TextcatTextfield[textfield]) ||
      this.getActiveRegion()?.enabledEditableFields?.includes(TextcatTextfield[textfield]) ||
      false
    );
  }

  public getActiveRegion(): RegionConfiguration | undefined {
    return this.activeRegion;
  }

  public getActiveRegionId(): string | undefined {
    return this.activeRegion?.id;
  }

  public setActiveRegion(region: RegionConfiguration | string) {
    if (!this.currentAuthor) {
      return;
    }
    region = this.getCurrentAuthorRegions().find((r) => r.id === (typeof region === "string" ? region : region.id));
    if (region) {
      this.activeRegion = region;
      this.activeRegionSubject.next(this.activeRegion);
      this.localStorageService.setActiveRegion(this.activeRegion);
    } else {
      this.logout();
    }
  }

  public isCurrentUserInRole(...roles0: (keyof typeof Enums.UserRole)[]): boolean {
    let roles: Enums.UserRole[] = this.currentAuthor?.roles ?? [];
    // the user may restrict themselves to a single effective role (e.g. an admin who does not want admin powers)
    const effectiveRole = this.localStorageService.effectiveRole;
    if (effectiveRole) {
      roles = roles.includes(effectiveRole) ? [effectiveRole] : [];
    }
    // if the user is an observer and has training mode enabled then they are temporarily upgraded to forecaster
    if (roles.includes(Enums.UserRole.OBSERVER) && this.localStorageService.isTrainingEnabled) {
      roles = roles.map((r) => (r === Enums.UserRole.OBSERVER ? Enums.UserRole.FORECASTER : r));
    }
    return roles0.some((role) => roles.includes(Enums.UserRole[role]));
  }

  public getCurrentAuthorRegions(): RegionConfiguration[] {
    return this.authentication?.regions.sort((a, b) => a.id.localeCompare(b.id)) || [];
  }

  public getInternalRegions(filter?: (r: RegionConfiguration) => boolean): string[] {
    return this.internalRegions
      .filter(filter || (() => true))
      .map((r) => r.id)
      .sort((a, b) => a.localeCompare(b));
  }

  public getInternalRegionsWithoutSuperRegions(filter?: (r: RegionConfiguration) => boolean): string[] {
    return this.internalRegions
      .filter((r) => r.subRegions && r.subRegions.length === 0)
      .filter(filter || (() => true))
      .map((r) => r.id)
      .sort((a, b) => a.localeCompare(b));
  }

  public getExternalServers(): ServerModel[] {
    return this.externalServers;
  }

  public isActiveRegion(region: string): boolean {
    return region?.startsWith(this.getActiveRegionId());
  }

  public isInternalRegion(region: string): boolean {
    return this.getInternalRegions().some((r) => region?.startsWith(r));
  }

  public isExternalRegion(region: string): boolean {
    if (this.isInternalRegion(region)) {
      return false;
    }
    for (const externalServer of this.externalServers) {
      if (externalServer.regions.includes(region)) return true;
    }
    return false;
  }
}

export const AuthenticationResponseSchema = z.object({
  user: UserSchema,
  regions: RegionConfigurationSchema.array(),
  access_token: z.string(),
  api_url: z.string().nullish(),
});
export type AuthenticationResponse = z.infer<typeof AuthenticationResponseSchema>;

/**
 * @deprecated
 */
export const AuthenticationResponseSchema2024 = z
  .object({
    email: z.string(),
    name: z.string(),
    roles: z.enum(Enums.UserRole).array(),
    regions: RegionConfigurationSchema.array(),
    access_token: z.string(),
    refresh_token: z.string().nullish(),
    api_url: z.string().nullish(),
  })
  .transform((a) =>
    AuthenticationResponseSchema.parse({
      user: {
        email: a.email,
        name: a.name,
        roles: a.roles,
      },
      regions: a.regions,
      access_token: a.access_token,
      api_url: a.api_url,
    }),
  );
